import type { Point } from "../scene/model";
export function bilinear(q: readonly Point[], u: number, v: number): Point {
  const [a, b, c, d] = q as [Point, Point, Point, Point];
  return {
    x:
      a.x * (1 - u) * (1 - v) +
      b.x * u * (1 - v) +
      c.x * u * v +
      d.x * (1 - u) * v,
    y:
      a.y * (1 - u) * (1 - v) +
      b.y * u * (1 - v) +
      c.y * u * v +
      d.y * (1 - u) * v,
  };
}
/** Inverse on the convex unit patch; coefficients are computed once per raster. */
export function inverseBilinear(
  quad: readonly Point[],
): (x: number, y: number) => Point {
  const [a, b, c, d] = quad as [Point, Point, Point, Point];
  const ex = b.x - a.x,
    ey = b.y - a.y,
    fx = d.x - a.x,
    fy = d.y - a.y,
    kx = a.x - b.x + c.x - d.x,
    ky = a.y - b.y + c.y - d.y,
    A = ey * kx - ex * ky,
    cross = ex * fy - ey * fx;
  return (x, y) => {
    const px = x - a.x,
      py = y - a.y,
      B = px * ky - py * kx - cross,
      C = px * fy - py * fx;
    const disc = B * B - 4 * A * C;
    if (disc < -1e-8) return { x: -1, y: -1 };
    const root = -0.5 * (B + (B < 0 ? -1 : 1) * Math.sqrt(Math.max(0, disc)));
    const roots =
      Math.abs(A) < 1e-12 ? [-C / B] : [root / A, root ? C / root : 0];
    for (const u of roots) {
      const dx = fx + kx * u,
        dy = fy + ky * u;
      const v =
        Math.abs(dx) > Math.abs(dy) ? (px - ex * u) / dx : (py - ey * u) / dy;
      if (
        Number.isFinite(u) &&
        Number.isFinite(v) &&
        u >= -1e-7 &&
        u <= 1 + 1e-7 &&
        v >= -1e-7 &&
        v <= 1 + 1e-7
      )
        return { x: u, y: v };
    }
    return { x: -1, y: -1 };
  };
}
